// 2120 plan entitlements + trial-aware effective plan.
// Source of truth for what each plan unlocks.
// Calm, non-blocking gating, we *show* upgrade prompts, we do not lock data.

export const PLAN_TIER = { free: 0, clarity: 1, horizon: 2, summit: 3 };

export const FEATURES = {
  // Free (0), explicit minimums so we can show "Available on Free" badges
  // and run feature checks consistently across pages.
  dashboard_basic:     { minTier: 'free', label: 'Basic dashboard' },
  transactions_manual: { minTier: 'free', label: 'Manual transactions' },
  clarity_score_basic: { minTier: 'free', label: 'Basic Clarity Score' },

  // Clarity (1), paid entry. AI features start here.
  dashboard:           { minTier: 'clarity', label: 'Dashboard with AI commentary' },
  transactions:        { minTier: 'clarity', label: 'Full transactions' },
  csv_import:          { minTier: 'clarity', label: 'CSV/PDF Import' },
  receipt_scan:        { minTier: 'clarity', label: 'Receipt scan' },
  recurring:           { minTier: 'clarity', label: 'Recurring expenses' },
  goals:               { minTier: 'clarity', label: 'Goals' },
  reflections_current: { minTier: 'clarity', label: 'Monthly reflection' },
  clarity_score:       { minTier: 'clarity', label: 'Financial Clarity Score (AI)' },
  ai_insights_basic:   { minTier: 'clarity', label: 'Basic AI insights' },

  // Horizon (2)
  net_worth:           { minTier: 'horizon', label: 'Net Worth', upsell: 'For a broader view of what you own and owe.' },
  accounts:            { minTier: 'horizon', label: 'Accounts', upsell: 'Track bank, demat, credit cards in one place.' },
  multi_currency:      { minTier: 'horizon', label: 'Multi-currency', upsell: 'When your life spans countries and currencies.' },
  reflections_history: { minTier: 'horizon', label: 'Reflections journal', upsell: 'Browse every month of financial evolution.' },
  ai_insights_deep:    { minTier: 'horizon', label: 'Advanced AI insights', upsell: 'Deeper monthly reflections and patterns.' },
  projections:         { minTier: 'horizon', label: 'Projections', upsell: 'See your financial trajectory over 1, 5, 10, 20 years.' },
  fi_tracker:          { minTier: 'horizon', label: 'Financial Independence', upsell: 'Track your FI progress over time.' },

  // Summit (3)
  family_shared:       { minTier: 'summit', label: 'Family / Partner sharing', upsell: 'A calm shared view for two.' },
  family_goals:        { minTier: 'summit', label: 'Family goals', upsell: 'Plan and align together.' },
  accountant_access:   { minTier: 'summit', label: 'Accountant access', upsell: 'Invite your accountant for read-only oversight.' },
};

export function planTier(plan) {
  return PLAN_TIER[plan] || 0;
}

// ----- Trial-aware effective plan ------------------------------------------
// Phase 45, Beta access wins over trial: if the user has been invited to
// the private beta and their access hasn't been revoked, they get full
// access to their `beta_plan` regardless of their nominal plan/trial state.
export function isBetaActive(profile) {
  if (!profile?.is_beta) return false;
  const ends = profile.beta_ends_at ? new Date(profile.beta_ends_at) : null;
  if (!ends) return true; // open-ended beta
  return ends > new Date();
}

// If the user's trial is active, they get full Horizon access regardless
// of their nominal plan. After the trial ends, they fall back to their plan
// (default 'free').
export function effectivePlan(profile) {
  if (!profile) return 'free';
  // 1) Beta takes precedence.
  if (isBetaActive(profile) && ['clarity', 'horizon', 'summit'].includes(profile.beta_plan)) {
    return profile.beta_plan;
  }
  // 2) Active trial.
  const trialEnds = profile.trial_ends_at ? new Date(profile.trial_ends_at) : null;
  if (trialEnds && trialEnds > new Date()) {
    return profile.trial_plan || 'horizon';
  }
  return profile.plan || 'free';
}

export function isOnTrial(profile) {
  if (!profile?.trial_ends_at) return false;
  // Phase 54 hotfix, when a user is in the Private Beta, their effective
  // plan is `beta_plan` (Beta takes precedence over Trial in
  // `effectivePlan`). Showing "Trial · 14 days left" badges or any trial
  // copy in Settings is then incorrect, there IS no active trial as far
  // as the user is concerned. So suppress trial UI for beta users.
  if (isBetaActive(profile)) return false;
  return new Date(profile.trial_ends_at) > new Date();
}

export function trialDaysLeft(profile) {
  if (!isOnTrial(profile)) return 0;
  const ends = new Date(profile.trial_ends_at);
  return Math.max(0, Math.ceil((ends - new Date()) / (1000 * 60 * 60 * 24)));
}

// ----- Feature checks -------------------------------------------------------
export function hasFeature(planOrProfile, featureKey) {
  const f = FEATURES[featureKey];
  if (!f) return true;
  // Accept either a plan string or a full profile object
  const plan = typeof planOrProfile === 'string'
    ? planOrProfile
    : effectivePlan(planOrProfile);
  return planTier(plan) >= planTier(f.minTier);
}

export function nextPlanFor(featureKey) {
  const f = FEATURES[featureKey];
  return f ? f.minTier : null;
}

export function planMeta(plan) {
  const map = {
    free:    { label: 'Free',    accent: 'neutral'   },
    clarity: { label: 'Clarity', accent: 'evergreen' },
    horizon: { label: 'Horizon', accent: 'gold'      },
    summit:  { label: 'Summit',  accent: 'evergreen' },
  };
  return map[plan] || map.free;
}

// ----- Plan-based soft limits ----------------------------------------------
// Calm caps, never hard-blocks. Hitting a cap shows an upgrade nudge.
// Source of truth: anything user-facing in pricing copy must come from here.
export const SOFT_LIMITS = {
  // Goals: how many active goals are allowed
  goals: {
    free:    1,
    clarity: 3,        // bumped from 1 (Phase 31 product call)
    horizon: 99,
    summit:  99,
  },
  // Recurring items: how many active recurring expenses allowed
  recurring: {
    free:    1,
    clarity: 5,        // bumped from 3 (Phase 31 product call)
    horizon: 99,
    summit:  99,
  },
  // Linked accounts (bank, card, demat etc)
  accounts: {
    free:    1,
    clarity: 3,
    horizon: 99,
    summit:  99,
  },
  // Transactions per calendar month, unlimited everywhere; Free is bounded
  // by the 30-day retention wall instead of a count cap (feels less petty).
  transactions_per_month: {
    free:    99999,
    clarity: 99999,
    horizon: 99999,
    summit:  99999,
  },
  // History retention in days (Free is the only tier with a wall).
  history_days: {
    free:    30,
    clarity: 365,
    horizon: 99999,
    summit:  99999,
  },
  // Statement (PDF/CSV) imports per calendar month.
  // Free gets ONE lifetime taster within the first 7 days, enforced server-
  // side, so the per-month value here is `0` after that window expires.
  statement_imports_per_month: {
    free:    0,        // gated by `free_pdf_taster` lifetime flag instead
    clarity: 4,
    horizon: 12,
    summit:  25,
  },
  // Receipt scans per calendar month.
  receipt_scans_per_month: {
    free:    0,
    clarity: 25,
    horizon: 100,
    summit:  200,  // mirrors backend; reflects two-partner household
  },
  // AI monthly reflections (generate / refresh count)
  reflections_per_month: {
    free:    0,
    clarity: 1,
    horizon: 6,
    summit:  6,
  },
  // Concurrent active sessions (Phase 34 enforcement; surfaced in copy now).
  concurrent_sessions: {
    free:    1,
    clarity: 2,
    horizon: 3,
    summit:  5,
  },
};

// One-time entitlements that aren't naturally a "per-month" or "per-N" cap.
// Useful for hooks like the Free PDF taster that's lifetime-scoped and
// time-windowed ("within the first 7 days of signup").
export const ONE_TIME_ENTITLEMENTS = {
  free_pdf_taster: {
    // Free users get exactly one statement import to feel the magic. After
    // the window expires (7 days from `profile.first_seen_at`) OR after they
    // burn it, the import flow gates with "Move to Clarity to import more".
    plans: ['free'],
    window_days: 7,
    count: 1,
  },
};

// Convenience helper, pulls the right limit for a plan
export function softLimit(featureKey, plan) {
  const m = SOFT_LIMITS[featureKey];
  if (!m) return Infinity;
  return m[plan] ?? m.free ?? Infinity;
}
